// The Tänään tab's data rules, without React: the current round's games, the
// round's points total, the points-per-day series (also behind Kausi's graphs),
// and the rows of the round's player table.

import GameStatus from '../../core/model/gameStatus';
import type { TeamAbbrev } from '../../core/model/teams';
import type { Game, Goalie, Player, PointsByDate, Skater } from '../../core/model/types';
import type { ScoreFilter } from '../../core/settings/settings';
import { toSeconds } from '../../core/model/coerce';
import { sortRows } from '../common/sort';
import { isGameLocked, sortGames } from './gameViewModel';

export const roundGames = (scores: readonly Game[], roundDate: string): Game[] =>
    sortGames(scores.filter(game => game.date === roundDate));

// Points of the tracked players in the games: goals and assists, not counting
// shootout goals.
export function countryPoints(games: readonly Game[], players: readonly Player[]): number {
    const ids = new Set(players.map(player => player.playerId));
    let points = 0;
    for (const game of games) {
        for (const goal of game.goals) {
            if (goal.time === '00:00') {
                continue;
            }
            for (const id of [goal.scorerId, goal.assistOneId, goal.assistTwoId]) {
                if (id !== undefined && ids.has(id)) {
                    points++;
                }
            }
        }
    }
    return points;
}

export interface RoundTotal {
    // undefined while the total would give something away, or before any game has started.
    points: number | undefined;
    // Why there is no total: scores are off or a game is hidden (as opposed to nothing played yet).
    locked: boolean;
}

// The round's points total. It is withheld when scores are switched off or any
// of the round's games is hidden (the total would disagree with what is on
// screen), and until a game has started.
export function buildRoundTotal(
    games: readonly Game[],
    players: readonly Player[],
    scoreFilter: ScoreFilter,
    hiddenTeams: readonly string[],
    revealed: ReadonlySet<string> = new Set(),
    teamAbbrev?: TeamAbbrev,
): RoundTotal {
    const locked = (scoreFilter === 'none' && games.length === 0) ||
        games.some(game => isGameLocked(game, scoreFilter, hiddenTeams, revealed, teamAbbrev));
    const anyStarted = games.some(game => !GameStatus.deriveStatus(game).isNotYetStarted);
    if (locked || !anyStarted) {
        return { points: undefined, locked };
    }
    return { points: countryPoints(games, players), locked };
}

export interface PointsSeries {
    dates: string[];
    values: number[];
    // The last value is the current round's (still being played, or finished tonight).
    includesRound: boolean;
}

// Points per game day, oldest first, up to the round shown (the current one or
// the previous game day), with the round's total as the last bar (or without it
// while the total is withheld).
export function buildPointsSeries(points: PointsByDate, roundDate: string, roundPoints: number | undefined): PointsSeries {
    const byDate: PointsByDate = Object.fromEntries(Object.entries(points).filter(([date]) => date < roundDate));
    if (roundPoints !== undefined) {
        byDate[roundDate] = roundPoints;
    }
    const dates = Object.keys(byDate).sort();
    return {
        dates,
        values: dates.map(date => byDate[date]),
        includesRound: roundPoints !== undefined,
    };
}

// ---- the round's player table ------------------------------------------------

export interface RoundSkaterRow {
    player: Skater;
    goals: number;
    assists: number;
    points: number;
    shots: number;
    plusMinus: number;
    timeOnIce: string;
    chip?: GameChip;
}

export interface RoundGoalieRow {
    player: Goalie;
    saves: number;
    shotsAgainst: number;
    savePct: number;
    timeOnIce: string;
    chip?: GameChip;
}

export interface GameChip {
    // "DAL 3–2": the player's team and the score from the team's point of view.
    label: string;
    live: boolean;
}

const chipFor = (player: Player, games: readonly Game[]): GameChip | undefined => {
    const game = games.find(candidate => candidate.homeTeamId === player.teamId || candidate.awayTeamId === player.teamId);
    if (game === undefined) {
        return undefined;
    }
    const home = game.homeTeamId === player.teamId;
    const own = home ? game.homeTeamGoals : game.awayTeamGoals;
    const other = home ? game.awayTeamGoals : game.homeTeamGoals;
    return { label: `${player.team} ${own}–${other}`, live: GameStatus.deriveStatus(game).isLive };
};

const byName = (a: Player, b: Player) => a.lastName.localeCompare(b.lastName, 'fi') || a.firstName.localeCompare(b.firstName, 'fi');

export const SKATER_ROUND_COLUMNS: Record<string, (row: RoundSkaterRow) => number | string | undefined> = {
    name: row => row.player.lastName,
    team: row => row.player.team,
    goalsLatest: row => row.goals,
    assistsLatest: row => row.assists,
    // Goals first: 1+0 ranks above 0+3.
    pointsLatest: row => row.goals * 1000 + row.assists,
    toiLatest: row => toSeconds(row.timeOnIce),
    plusMinusLatest: row => row.plusMinus,
    shotsLatest: row => row.shots,
};

export const GOALIE_ROUND_COLUMNS: Record<string, (row: RoundGoalieRow) => number | string | undefined> = {
    name: row => row.player.lastName,
    team: row => row.player.team,
    savesLatest: row => row.saves,
    shotsAgainstLatest: row => row.shotsAgainst,
    savePctLatest: row => row.savePct,
    toiLatest: row => toSeconds(row.timeOnIce),
};

// A player whose game is hidden (or, without a game on the list, whose team is):
// anything shown about them could give the hidden game's result away.
export function isPlayerWithheld(
    player: Player,
    games: readonly Game[],
    hiddenTeams: readonly string[],
    revealed: ReadonlySet<string> = new Set(),
    teamAbbrev?: TeamAbbrev,
): boolean {
    const game = games.find(candidate => candidate.homeTeamId === player.teamId || candidate.awayTeamId === player.teamId);
    return game === undefined ? hiddenTeams.includes(player.team) : isGameLocked(game, 'all', hiddenTeams, revealed, teamAbbrev);
}

// The round's stat lines of the tracked players who played in it, from their
// latest game. Players of hidden teams are left out: their line would give the
// hidden game's result away.
export function buildRoundPlayers(
    players: readonly Player[],
    games: readonly Game[],
    roundDate: string,
    hiddenTeams: readonly string[],
    skaterOrder: string,
    goalieOrder: string,
    revealed: ReadonlySet<string> = new Set(),
    teamAbbrev?: TeamAbbrev,
): { skaters: RoundSkaterRow[]; goalies: RoundGoalieRow[]; withheld: boolean } {
    const playedAll = players.filter(player => player.latestGame?.date === roundDate);
    const played = playedAll.filter(player => !isPlayerWithheld(player, games, hiddenTeams, revealed, teamAbbrev));

    const skaters: RoundSkaterRow[] = played.flatMap(player => {
        if (player.kind !== 'skater' || player.latestGame === undefined) {
            return [];
        }
        const { goals, assists, shots, plusMinus, timeOnIce } = player.latestGame;
        return [{ player, goals, assists, points: goals + assists, shots, plusMinus, timeOnIce, chip: chipFor(player, games) }];
    });
    const goalies: RoundGoalieRow[] = played.flatMap(player => {
        if (player.kind !== 'goalie' || player.latestGame === undefined) {
            return [];
        }
        const { saves, shotsAgainst, savePct, toi } = player.latestGame;
        return [{ player, saves, shotsAgainst, savePct, timeOnIce: toi, chip: chipFor(player, games) }];
    });

    return {
        withheld: played.length < playedAll.length,
        skaters: sortRows(skaters, skaterOrder, SKATER_ROUND_COLUMNS, (a, b) => byName(a.player, b.player)),
        goalies: sortRows(goalies, goalieOrder, GOALIE_ROUND_COLUMNS, (a, b) => byName(a.player, b.player)),
    };
}
